import HttpResponseCodes from './../HttpResponseCodes';
import AuthUrlMap from '../AuthUrlMap';
import isEmpty from 'lodash/isEmpty';

/**
 *
 */
class AxiosInterceptor {

    /**
     *
     * @param options
     * @returns {AxiosInterceptor}
     */
    config(options) {
        if (options.hasOwnProperty('store')) {
            this.store = options.store;
        }
        if (options.hasOwnProperty('router')) {
            this.router = options.router;
        }
        if (options.hasOwnProperty('xhrLib')) {
            this.xhrLib = options.xhrLib;
        }
        if (options.hasOwnProperty('authorizationHeaderKey')) {
            this.authorizationHeaderKey = options.authorizationHeaderKey;
        }
        if (options.hasOwnProperty('unifiedLogin')) {
            this.unifiedLogin = options.unifiedLogin;
        }
        if (options.hasOwnProperty('internalDomains')) {
            this.internalDomains = options.internalDomains;
        }
        return this;
    }

    /**
     *
     * @returns {boolean}
     */
    isLoggedIn() {
        return this.hasOwnProperty('store') &&
            this.store.getters.authToken !== null;
    }

    /**
     *
     * @returns {RequestInterceptor}
     */
    interceptRequests() {

        /**
         * @todo implement Axios check
         */
        // if (
        //     typeof this.xhrLib !== 'function' &&
        //     this.xhrLib.constructor.name !== 'Axios'
        // ) {
        //     return this;
        // }

        this.xhrLib.interceptors.request.use(
            request => this.requestInterceptor(request),
        );

        this.xhrLib.interceptors.response.use(
            response => this.responseInterceptor(response),
            error => this.errorInterceptor(error),
        );
    }

    async getRefreshTokenPromise() {
        if (
            !this.hasOwnProperty('_refreshTokenPromise') ||
            isEmpty(this._refreshTokenPromise)
        ) {
            if (!this.store.getters.isRefreshingToken) {
                this._refreshTokenPromise = this.store.dispatch('refreshToken', this.store.getters.refreshToken);
            }
        }
        return this._refreshTokenPromise;
    }

    removeRefreshTokenPromise() {
        this._refreshTokenPromise = null;
    }

    /**
     * Filters all requests going through axios
     * @param request
     * @returns {*}
     */
    requestInterceptor(request) {
        if (!this.isLoggedIn()) {
            return request;
        }
        //Set Authorization Header
        request.headers.common[this.authorizationHeaderKey] = `Bearer ${this.store.getters.authToken}`;

        return request
    }

    /**
     * Filters all successful axios response
     * @param response
     * @returns {*}
     */
    responseInterceptor(response) {
        /**
         * @todo implement response filter if needed
         */
        return response
    };
    /**
     * Filters all failed axios requests
     * @param error
     * @returns {Promise}
     */
    async errorInterceptor(error) {
        const excludedUrls = [
            AuthUrlMap.REFRESH_TOKEN_URL,
            AuthUrlMap.OAUTH_ME_XHR_URL,
            AuthUrlMap.LOGOUT_URL,
            AuthUrlMap.ME_URL,
        ];

        const status = error?.response?.status;

        // Check if request URL is from your domain
        const isInternalDomain = this.isUrlFromInternalDomain(error.config.url);

        // Only proceed with token refresh logic if it's from your domain
        if (!isInternalDomain) {
            return Promise.reject({...error});
        }

        //Internal Server Error || unauthorized for refresh token request
        if (
            excludedUrls.indexOf(error.config.url) > -1 &&
            status !== HttpResponseCodes.HTTP_OK
        ) {
            console.log('Default auth invalid response.');
            if (this.unifiedLogin) {
                return this.store.dispatch('redirectToLogin');
            }
            return this.store.dispatch('logout');
        }

        //Unauthorised
        if (status === HttpResponseCodes.HTTP_UNAUTHORISED) {
            if (error.config.hasOwnProperty('isRetry')) {
                console.log('Retry failed with 401.');
                if (this.unifiedLogin) {
                    return this.store.dispatch('redirectToLogin');
                }
                return this.store.dispatch('logout');
            }

            if (this.isLoggedIn()) {
                const isRefreshingToken = !this.store.getters.isRefreshingToken;
                console.log('Refreshing token.');
                //Refresh Token on 401
                await this.getRefreshTokenPromise();
                if (!isRefreshingToken) {
                    this.removeRefreshTokenPromise();
                }
                const retryConfig = {
                    ...error.config,
                    isRetry: true,
                };
                if (this.store.getters.authToken) {
                    console.log('Retrying XHR.');
                    return this.xhrLib(retryConfig);
                }
            }

            if (
                this.hasOwnProperty('store') &&
                error.config.url.indexOf(AuthUrlMap.OAUTH_ME_XHR_URL) === -1
            ) {
                console.log('401 last chance.');
                if (this.unifiedLogin) {
                    return this.store.dispatch('redirectToLogin');
                }
                return this.store.dispatch('logout');
            }
        }

        return Promise.reject({...error});
    }

    /**
     * Check if the URL is from internal domain
     * @param {string} url - The URL to check
     * @returns {boolean} - True if URL is from internal domain
     */
    isUrlFromInternalDomain(url) {
        // Skip checking relative URLs as they're always from your domain
        if (url.startsWith('/')) {
            return true;
        }

        try {
            const internalDomains = this.internalDomains;

            // Parse the URL to get its hostname
            const urlObj = new URL(url);

            // Check if hostname ends with any of the internal domains
            return internalDomains.some(domain =>
                urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
            );
        } catch (e) {
            // If URL parsing fails, assume it's external
            return false;
        }
    }
}

export default new AxiosInterceptor();
